<template>
  <div class="package-usage" v-if="packageUsage">
    <h3 v-if="noIcon" class="chart-header">Usage in {{ title }}</h3>
    <div v-else class="usage-info">
      <h2 class="usage-info__header">
        <svg>
          <use v-bind="{ 'xlink:href': '#usage-icon'}" />
        </svg>
        Usage in {{ title }}
      </h2>
      <router-link v-if="projectKey"
        :to="{ name: 'usageReport', params: { projectKey } }"
        class="usage-info__desc--link">
        Explore
      </router-link>
    </div>
    <div class="package-usage__wrapper">
      <div class="package-usage__item">
        <div class="package-usage__label">
          <span class="package-usage__name">API Hits</span>
          <span class="package-usage__count"
            :class="{ 'package-usage__count--over': packageUsage.limit.hits > 0
              && packageUsage.used.hits >= packageUsage.limit.hits }">
            {{ (packageUsage.limit.hits > 0 && packageUsage.used.hits >= packageUsage.limit.hits
              ? packageUsage.limit.hits : packageUsage.used.hits).toLocaleString('en-US') }}
            <template v-if="packageUsage.limit.hits > 0">
              / {{ packageUsage.limit.hits.toLocaleString('en-US') }}
            </template>
          </span>
        </div>
        <div class="package-usage__bar" v-if="packageUsage.limit.hits > 0">
          <div class="package-usage__bar-fill"
            :style="{ width: Math.min((packageUsage.used.hits / packageUsage.limit.hits) * 100, 100) + '%' }"
            :class="{ 'package-usage__bar-fill--over': packageUsage.used.hits >= packageUsage.limit.hits }">
          </div>
        </div>
      </div>
      <div class="package-usage__item">
        <div class="package-usage__label">
          <span class="package-usage__name">Matches</span>
          <span class="package-usage__count"
            :class="{ 'package-usage__count--over': packageUsage.limit.matches > 0
              && packageUsage.used.matches >= packageUsage.limit.matches }">
            {{ (packageUsage.limit.matches > 0 && packageUsage.used.matches >= packageUsage.limit.matches
              ? packageUsage.limit.matches : packageUsage.used.matches).toLocaleString('en-US') }}
            <template v-if="packageUsage.limit.matches > 0">
              / {{ packageUsage.limit.matches.toLocaleString('en-US') }}
            </template>
          </span>
        </div>
        <div class="package-usage__bar" v-if="packageUsage.limit.matches > 0">
          <div class="package-usage__bar-fill"
            :style="{ width: Math.min((packageUsage.used.matches / packageUsage.limit.matches) * 100, 100) + '%' }"
            :class="{ 'package-usage__bar-fill--over': packageUsage.used.matches >= packageUsage.limit.matches }">
          </div>
        </div>
        <p v-if="hitsExhausted && !matchesExhausted"
          class="package-usage__hits-disabled-note">
          Match access is also paused due to API hits limit
        </p>
      </div>
      <a v-if="isExhausted" href="mailto:support@roanuz.com"
        class="btn-style-common medium" style="width: fit-content;">
        Contact Support
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PackageUsage',
  props: {
    packageUsage: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: 'This Month',
    },
    noIcon: {
      type: Boolean,
      default: false,
    },
    projectKey: {
      type: String,
      default: null,
    },
  },
  computed: {
    hitsExhausted() {
      const u = this.packageUsage;
      if (!u) return false;
      return u.limit.hits > 0 && u.used.hits >= u.limit.hits;
    },
    matchesExhausted() {
      const u = this.packageUsage;
      if (!u) return false;
      return u.limit.matches > 0 && u.used.matches >= u.limit.matches;
    },
    isExhausted() {
      return this.hitsExhausted || this.matchesExhausted;
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-header {
  @include font-styles(
    $type: 'text',
    $size: 'lg',
    $weight: 'bold'
  );
  color: var(--global--main--title--Color);
  padding-bottom: size(14);
}

.usage-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: size(14);
  &__header {
    @include font-styles(
      $type: 'text',
      $size: 'lg',
      $weight: 'semibold'
    );
    color: var(--global--main--title--Color);
    display: flex;
    align-items: center;
    gap: size(8);
    svg {
      fill: var(--global--usage-img--Color);
      width: size(19);
      height: size(19);
      display: inline-block;
      vertical-align: middle;
    }
  }
}

.package-usage {
  margin-top: size(30);
  margin-bottom: size(30);
  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: size(24);
    background: var(--global--white--Color);
    box-shadow: 0 size(1) size(3) rgba(0, 0, 0, 0.1), 0 size(2) size(1) rgba(0, 0, 0, 0.06),
      0 size(1) size(1) rgba(0, 0, 0, 0.08);
    border-radius: size(8);
    padding: size(30);
  }
  &__summary {
    margin-bottom: size(20);
    h4 {
      @include font-styles(
        $type: 'label',
        $size: 'lg',
        $weight: 'normal'
      );
    }
  }
  &__item {
    display: flex;
    flex-direction: column;
    gap: size(8);
  }
  &__label {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__name {
    @include font-styles(
      $type: 'label',
      $size: 'lg',
      $weight: 'normal'
    );
  }
  &__count {
    @include font-styles(
      $type: 'text',
      $size: 'lg',
      $weight: 'bold'
    );
    &--over {
      color: #ef4444;
    }
  }
  &__bar {
    background: #e5e7eb;
    border-radius: size(4);
    height: size(8);
    overflow: hidden;
  }
  &__hits-disabled-note {
    @include font-styles($type: 'label', $size: 'sm', $weight: 'regular');
    color: #b45309;
    margin-top: size(4);
  }
  &__bar-fill {
    height: 100%;
    background: #0085FF;
    border-radius: size(4);
    transition: width 0.3s ease;
    &--over {
      background: #ef4444;
    }
  }
}
</style>
